import React, { useState, useEffect } from 'react';
import { apiCall } from '../utils/api';
import { toast } from '../components/ui/sonner';
import {
  Clock, Save, ToggleLeft, ToggleRight, DollarSign, Plus, Trash2,
  AlertTriangle, Moon, Sun,
} from 'lucide-react';

// Check whether an "end < start" window wraps overnight (e.g. 23:00 → 07:00)
const isOvernight = (w) => (w.start && w.end && w.end <= w.start);

// Total minutes covered by a window, handling overnight wrap.
const minutes = (w) => {
  if (!w.start || !w.end) return 0;
  const [sh, sm] = w.start.split(':').map(Number);
  const [eh, em] = w.end.split(':').map(Number);
  const startMin = sh * 60 + sm;
  let endMin = eh * 60 + em;
  if (endMin <= startMin) endMin += 24 * 60; // overnight
  return endMin - startMin;
};

// Detect overlapping windows on the same day (excluding overnight wrap cases,
// which are treated as a single continuous segment).
const hasOverlap = (windows) => {
  const ranges = windows
    .filter(w => w.start && w.end)
    .map(w => {
      const [sh, sm] = w.start.split(':').map(Number);
      const [eh, em] = w.end.split(':').map(Number);
      let s = sh * 60 + sm;
      let e = eh * 60 + em;
      if (e <= s) e += 24 * 60;
      return [s, e];
    })
    .sort((a, b) => a[0] - b[0]);
  for (let i = 1; i < ranges.length; i++) {
    if (ranges[i][0] < ranges[i - 1][1]) return true;
  }
  return false;
};

const formatHours = (mins) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
};

export default function AvailabilityManager() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadAvailability(); }, []);

  const loadAvailability = async () => {
    try {
      const res = await apiCall('/api/worker/my-availability');
      setData(res.availability);
    } catch {
      toast.error('Failed to load availability');
    } finally {
      setLoading(false);
    }
  };

  const updateDay = (i, mutator) => {
    setData(prev => {
      const next = { ...prev, schedule: prev.schedule.map((d, di) => di === i ? mutator({ ...d, windows: [...(d.windows || [])] }) : d) };
      return next;
    });
  };

  const toggleDay = (i) => updateDay(i, d => ({ ...d, available: !d.available }));

  const addWindow = (i) => updateDay(i, d => {
    const existing = d.windows || [];
    const nextStart = existing.length ? existing[existing.length - 1].end : '08:00';
    return { ...d, windows: [...existing, { start: nextStart, end: '17:00' }] };
  });

  const removeWindow = (i, wIdx) => updateDay(i, d => {
    const next = (d.windows || []).filter((_, k) => k !== wIdx);
    return { ...d, windows: next.length ? next : [{ start: '08:00', end: '17:00' }] };
  });

  const updateWindow = (i, wIdx, field, value) => updateDay(i, d => {
    const next = (d.windows || []).map((w, k) => k === wIdx ? { ...w, [field]: value } : w);
    return { ...d, windows: next };
  });

  const handleSave = async () => {
    // Block save if any overlaps
    const bad = data.schedule.find(d => d.available && hasOverlap(d.windows || []));
    if (bad) {
      toast.error(`${bad.day} has overlapping windows. Please fix before saving.`);
      return;
    }
    setSaving(true);
    try {
      await apiCall('/api/worker/my-availability', {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      toast.success('Availability saved!');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-10 w-10 border-4 border-violet-600 border-t-transparent" />
    </div>
  );
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="availability-manager">
      {/* Header */}
      <div className="bg-gradient-to-br from-violet-600 to-purple-700 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white/15 rounded-xl backdrop-blur-sm">
              <Clock className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">My Availability</h2>
              <p className="text-white/80 text-sm">Add multiple time windows per day — overnight wraps supported.</p>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2.5 bg-white/20 backdrop-blur-sm text-white rounded-xl font-medium hover:bg-white/30 transition flex items-center gap-2 disabled:opacity-50"
            data-testid="save-availability-btn"
          >
            <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Weekly Schedule */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <h3 className="font-bold text-gray-900 mb-1">Weekly Schedule</h3>
        <p className="text-xs text-gray-500 mb-4">
          Tip: Set start ≤ end for same-day windows, or end ≤ start (e.g. 23:00 → 07:00) for overnight shifts.
        </p>
        <div className="space-y-3">
          {data.schedule.map((day, i) => {
            const overlap = day.available && hasOverlap(day.windows || []);
            const totalMin = (day.windows || []).reduce((s, w) => s + minutes(w), 0);
            return (
              <div
                key={day.day}
                className={`rounded-xl border transition-all ${
                  day.available
                    ? (overlap ? 'bg-rose-50 border-rose-300' : 'bg-violet-50 border-violet-200')
                    : 'bg-gray-50 border-gray-200'
                }`}
                data-testid={`avail-day-${day.day}`}
              >
                <div className="flex items-center gap-3 p-3 flex-wrap">
                  <button onClick={() => toggleDay(i)} className="flex-shrink-0" data-testid={`toggle-${day.day}`} aria-label={`Toggle ${day.day}`}>
                    {day.available ? (
                      <ToggleRight className="w-8 h-8 text-violet-600" />
                    ) : (
                      <ToggleLeft className="w-8 h-8 text-gray-400" />
                    )}
                  </button>
                  <span className={`w-24 font-semibold ${day.available ? 'text-gray-900' : 'text-gray-400'}`}>{day.day}</span>

                  {day.available ? (
                    <div className="flex-1 min-w-0 space-y-2">
                      {(day.windows || []).map((w, wi) => {
                        const overnight = isOvernight(w);
                        return (
                          <div key={wi} className="flex items-center gap-2 flex-wrap" data-testid={`window-${day.day}-${wi}`}>
                            <Sun className={`w-4 h-4 flex-shrink-0 ${overnight ? 'text-gray-300' : 'text-amber-500'}`} />
                            <input
                              type="time"
                              value={w.start}
                              onChange={(e) => updateWindow(i, wi, 'start', e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 outline-none"
                              data-testid={`${day.day}-window-${wi}-start`}
                            />
                            <span className="text-gray-400 text-sm">→</span>
                            <input
                              type="time"
                              value={w.end}
                              onChange={(e) => updateWindow(i, wi, 'end', e.target.value)}
                              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 outline-none"
                              data-testid={`${day.day}-window-${wi}-end`}
                            />
                            {overnight && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded">
                                <Moon className="w-2.5 h-2.5" /> Overnight
                              </span>
                            )}
                            <span className="text-xs text-gray-500 ml-auto">{formatHours(minutes(w))}</span>
                            {(day.windows || []).length > 1 && (
                              <button
                                onClick={() => removeWindow(i, wi)}
                                className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-100 transition"
                                title="Remove this window"
                                data-testid={`${day.day}-remove-${wi}`}
                                aria-label="Remove window"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        );
                      })}
                      <div className="flex items-center justify-between gap-2 pt-1">
                        <button
                          onClick={() => addWindow(i)}
                          className="text-xs font-semibold text-violet-700 hover:text-violet-800 flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-violet-100 transition"
                          data-testid={`${day.day}-add-window`}
                        >
                          <Plus className="w-3.5 h-3.5" /> Add another window
                        </button>
                        <span className="text-[11px] font-medium text-gray-500">
                          Total: <span className="text-gray-700 font-bold">{formatHours(totalMin)}</span>
                        </span>
                      </div>
                      {overlap && (
                        <div className="flex items-center gap-1.5 text-xs text-rose-700 bg-rose-100 rounded-lg px-2.5 py-1.5 mt-2" data-testid={`${day.day}-overlap-warning`}>
                          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                          Overlapping windows — resolve before saving.
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400 italic">Not available</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Preferences */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <h3 className="font-bold text-gray-900 mb-4">Preferences</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
              <Clock className="w-4 h-4" /> Max Hours/Week
            </label>
            <input
              type="number"
              value={data.max_hours_per_week || 40}
              onChange={(e) => setData({ ...data, max_hours_per_week: parseInt(e.target.value, 10) || 0 })}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-violet-500 outline-none"
              data-testid="max-hours-input"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
              <DollarSign className="w-4 h-4" /> Min Pay Rate ($/hr)
            </label>
            <input
              type="number"
              value={data.min_pay_rate || 15}
              onChange={(e) => setData({ ...data, min_pay_rate: parseInt(e.target.value, 10) || 0 })}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-violet-500 outline-none"
              data-testid="min-rate-input"
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <button
              type="button"
              onClick={() => setData({ ...data, open_to_overtime: !data.open_to_overtime })}
              className="flex-shrink-0"
              data-testid="toggle-overtime"
            >
              {data.open_to_overtime ? (
                <ToggleRight className="w-8 h-8 text-violet-600" />
              ) : (
                <ToggleLeft className="w-8 h-8 text-gray-400" />
              )}
            </button>
            <span className="text-sm font-medium text-gray-700">Open to overtime shifts</span>
          </label>
        </div>
      </div>
    </div>
  );
}
